import { el } from './dom';
import { renderDateField, DateStrings } from './date-picker';
import { FormField } from './types';

// Shared saved-Form field renderer (15 field types). Extracted from surveys.ts so both
// the survey `form`-question and the operator-sent chat form render identically. Each
// field writes into `values[field.key]`; `sync()` runs after every change so the caller
// can recompute "is the form filled / valid". (docs/forms-in-chat.md, docs/widget.md W4)

export interface FieldStrings extends DateStrings {
  choose: string;
  textPlaceholder: string;
  yes: string;
}

/** render every field into one `.cb-form` container */
export function renderFormFields(
  fields: FormField[],
  values: Record<string, unknown>,
  sync: () => void,
  t: FieldStrings,
): HTMLElement {
  const list = el('div', { class: 'cb-form' });
  for (const field of fields) list.appendChild(renderFormField(field, values, sync, t));
  return list;
}

export function renderFormField(
  field: FormField,
  values: Record<string, unknown>,
  sync: () => void,
  t: FieldStrings,
): HTMLElement {
  const row = el('div', { class: 'cb-formfield' });
  const label = el('label', { class: 'cb-flabel', text: field.label });
  if (field.isRequired) label.appendChild(el('span', { class: 'cb-req', text: ' *' }));
  row.appendChild(label);

  const key = field.key;
  const rules = field.validationRules ?? {};

  switch (field.type) {
    case 'textarea': {
      const area = el('textarea', {
        class: 'cb-textarea',
        attrs: {
          placeholder: field.placeholder || t.textPlaceholder,
          maxlength: '2000',
          'aria-label': field.label,
        },
        on: {
          input: (ev) => {
            values[key] = (ev.target as HTMLTextAreaElement).value;
            sync();
          },
        },
      });
      row.appendChild(area);
      break;
    }

    case 'select':
    case 'province':
    case 'city': {
      const options = field.options ?? [];
      if (options.length) {
        const sel = el('select', {
          class: 'cb-finput',
          attrs: { 'aria-label': field.label },
          on: {
            change: (ev) => {
              values[key] = (ev.target as HTMLSelectElement).value;
              sync();
            },
          },
        });
        sel.appendChild(el('option', { text: t.choose, attrs: { value: '' } }));
        for (const o of options) sel.appendChild(el('option', { text: o, attrs: { value: o } }));
        row.appendChild(sel);
      } else {
        // province/city carry no inline dataset — fall back to free text
        row.appendChild(textField(field, values, sync));
      }
      break;
    }

    case 'checkbox': {
      values[key] = [];
      const group = el('div', { attrs: { role: 'group', 'aria-label': field.label } });
      for (const o of field.options ?? []) {
        const box = el('input', {
          attrs: { type: 'checkbox', value: o },
          on: {
            change: (ev) => {
              const set = new Set(values[key] as string[]);
              if ((ev.target as HTMLInputElement).checked) set.add(o);
              else set.delete(o);
              values[key] = Array.from(set);
              sync();
            },
          },
        });
        group.appendChild(el('label', { class: 'cb-check' }, [box, el('span', { text: o })]));
      }
      row.appendChild(group);
      break;
    }

    case 'radio': {
      const name = `cb-${key}-${Math.random().toString(36).slice(2, 7)}`;
      const group = el('div', { attrs: { role: 'radiogroup', 'aria-label': field.label } });
      for (const o of field.options ?? []) {
        const radio = el('input', {
          attrs: { type: 'radio', name, value: o },
          on: {
            change: (ev) => {
              values[key] = (ev.target as HTMLInputElement).value;
              sync();
            },
          },
        });
        group.appendChild(el('label', { class: 'cb-check' }, [radio, el('span', { text: o })]));
      }
      row.appendChild(group);
      break;
    }

    case 'boolean': {
      const box = el('input', {
        attrs: { type: 'checkbox' },
        on: {
          change: (ev) => {
            values[key] = (ev.target as HTMLInputElement).checked;
            sync();
          },
        },
      });
      row.appendChild(
        el('label', { class: 'cb-check' }, [box, el('span', { text: field.placeholder || t.yes })]),
      );
      break;
    }

    case 'file': {
      const accept = (rules.types ?? [])
        .map((ty) => (ty.startsWith('.') ? ty : `.${ty}`))
        .join(',');
      const input = el('input', {
        class: 'cb-finput',
        attrs: {
          type: 'file',
          'aria-label': field.label,
          ...(accept ? { accept } : {}),
        },
        on: {
          change: (ev) => {
            // files aren't uploaded here — record the chosen name for context
            values[key] = (ev.target as HTMLInputElement).files?.[0]?.name ?? '';
            sync();
          },
        },
      });
      row.appendChild(input);
      break;
    }

    case 'date':
      row.appendChild(renderDateField(key, field.label, values, sync, t));
      break;

    case 'number': {
      const input = el('input', {
        class: 'cb-finput',
        attrs: {
          type: 'number',
          placeholder: field.placeholder || '',
          'aria-label': field.label,
          ...(rules.min != null ? { min: String(rules.min) } : {}),
          ...(rules.max != null ? { max: String(rules.max) } : {}),
        },
        on: {
          input: (ev) => {
            const v = (ev.target as HTMLInputElement).value;
            values[key] = v === '' ? '' : Number(v);
            sync();
          },
        },
      });
      row.appendChild(input);
      break;
    }

    default:
      // text, nationalCode, identitySerialNumber, licensePlate, motorcycleLicensePlate
      row.appendChild(textField(field, values, sync));
  }

  return row;
}

function textField(
  field: FormField,
  values: Record<string, unknown>,
  sync: () => void,
): HTMLElement {
  const rules = field.validationRules ?? {};
  return el('input', {
    class: 'cb-finput',
    attrs: {
      type: 'text',
      placeholder: field.placeholder || '',
      maxlength: '500',
      'aria-label': field.label,
      ...(rules.pattern ? { pattern: rules.pattern } : {}),
    },
    on: {
      input: (ev) => {
        values[field.key] = (ev.target as HTMLInputElement).value;
        sync();
      },
    },
  });
}
